import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Radio } from "lucide-react";
import PulseAdvisoryModal from "./PulseAdvisoryModal";

/**
 * NewsTicker — Live TaxSetu-branded regulatory advisory marquee.
 * Data source: /api/pulse/latest (rewritten in-house advisories, refreshed every 2h).
 * Each chip is TaxSetu voice — NO external URLs / attributions.
 * Click opens the full 4-section advisory modal in-app.
 */

const CATEGORY_ACCENTS = {
  "Income Tax": "text-emerald-300 border-emerald-500/40",
  "GST": "text-cyan-300 border-cyan-500/40",
  "Labour Law": "text-amber-300 border-amber-500/40",
  "Companies Act": "text-fuchsia-300 border-fuchsia-500/40",
  "FEMA/RBI": "text-sky-300 border-sky-500/40",
  "Capital Markets": "text-violet-300 border-violet-500/40",
  "Case Laws": "text-rose-300 border-rose-500/40",
  "Stock Markets": "text-lime-300 border-lime-500/40",
  "Mixed": "text-slate-300 border-slate-500/40",
};

const getApiBaseUrl = () => {
  const envUrl = process.env.REACT_APP_BACKEND_URL;
  const currentOrigin = typeof window !== "undefined" ? window.location.origin : "";
  if (
    currentOrigin &&
    !currentOrigin.includes("preview.emergentagent.com") &&
    !currentOrigin.includes("emergent.host")
  ) {
    return currentOrigin;
  }
  return envUrl;
};

export default function NewsTicker() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activePost, setActivePost] = useState(null);

  useEffect(() => {
    let cancelled = false;
    let intervalId = null;
    const fetchNews = async () => {
      try {
        const res = await fetch(`${getApiBaseUrl()}/api/pulse/latest?limit=15`);
        if (!res.ok) throw new Error("Failed to load");
        const data = await res.json();
        if (!cancelled) setItems((data && data.posts) || []);
      } catch {
        if (!cancelled) setItems([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchNews();
    // Poll every 20 s until we have items, then slow down to 5 min.
    intervalId = setInterval(() => {
      fetchNews();
    }, 20 * 1000);
    return () => {
      cancelled = true;
      if (intervalId) clearInterval(intervalId);
    };
  }, []);

  // Once we have items, slow the poll cadence to 5 min via a second effect
  useEffect(() => {
    if (!items.length) return;
    const id = setInterval(async () => {
      try {
        const res = await fetch(`${getApiBaseUrl()}/api/pulse/latest?limit=15`);
        if (res.ok) {
          const data = await res.json();
          setItems((data && data.posts) || []);
        }
      } catch {
        /* silent */
      }
    }, 5 * 60 * 1000);
    return () => clearInterval(id);
  }, [items.length > 0]);

  const looped = useMemo(() => (items.length ? [...items, ...items] : []), [items]);

  // While the feed is bootstrapping (fresh deploy) keep a compact skeleton bar
  // so the user knows a live pulse lives here — do not vanish completely.
  if (loading || !items.length) {
    return (
      <div
        className="w-full bg-slate-950/90 border-y border-white/10 backdrop-blur-md"
        data-testid="news-ticker-bar"
        role="region"
        aria-label="Live TaxSetu advisory ticker"
      >
        <div className="relative flex items-center overflow-hidden">
          <div className="flex items-center gap-1.5 px-3 sm:px-4 py-2.5 bg-gradient-to-r from-rose-600/90 to-red-600/90 flex-shrink-0 z-10">
            <span className="relative flex h-1.5 w-1.5">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-75" />
              <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-white" />
            </span>
            <Radio className="w-3 h-3 text-white" />
            <span className="text-[10px] sm:text-[11px] font-black tracking-widest text-white uppercase">
              Live
            </span>
          </div>
          <div className="flex-1 py-2.5 px-4 text-[12px] text-slate-400">
            Loading the latest tax, GST, EPFO & MCA pulse…
          </div>
        </div>
      </div>
    );
  }

  // Strip the mandatory prefix so the ticker chips read cleanly
  const cleanTitle = (t) =>
    (t || "")
      .replace(/^TaxSetu AI Alert:\s*/i, "")
      .replace(/^TaxSetu Insight:\s*/i, "");

  return (
    <>
      <div
        className="w-full bg-slate-950/90 border-y border-white/10 backdrop-blur-md shadow-[0_-1px_0_rgba(255,255,255,0.03)_inset]"
        data-testid="news-ticker-bar"
        role="region"
        aria-label="Live TaxSetu advisory ticker"
      >
        <div className="relative flex items-center overflow-hidden">
          {/* LIVE badge */}
          <div className="flex items-center gap-1.5 px-3 sm:px-4 py-2.5 bg-gradient-to-r from-rose-600/90 to-red-600/90 flex-shrink-0 z-10">
            <span className="relative flex h-1.5 w-1.5">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-75" />
              <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-white" />
            </span>
            <Radio className="w-3 h-3 text-white" />
            <span className="text-[10px] sm:text-[11px] font-black tracking-widest text-white uppercase">
              Live
            </span>
          </div>

          {/* Scrolling area */}
          <div className="flex-1 overflow-hidden relative">
            <div className="pointer-events-none absolute left-0 top-0 h-full w-8 bg-gradient-to-r from-slate-950 to-transparent z-10" />
            <div className="pointer-events-none absolute right-0 top-0 h-full w-8 bg-gradient-to-l from-slate-950 to-transparent z-10" />

            <div
              className="flex items-center gap-8 py-2.5 whitespace-nowrap hover:[animation-play-state:paused]"
              style={{ animation: "taxsetu-marquee 90s linear infinite" }}
            >
              {looped.map((p, i) => {
                const cat = p.category || "Mixed";
                const acc = CATEGORY_ACCENTS[cat] || CATEGORY_ACCENTS.Mixed;
                return (
                  <button
                    key={`${p.slug}-${i}`}
                    onClick={() => setActivePost(p)}
                    data-testid={`news-ticker-item-${i}`}
                    className="group inline-flex items-center gap-2.5 text-[12px] sm:text-[13px] hover:text-white transition-colors cursor-pointer bg-transparent border-0 outline-none"
                  >
                    <span
                      className={`inline-block text-[9.5px] font-bold uppercase tracking-widest border rounded-full px-2 py-0.5 ${acc}`}
                    >
                      {cat}
                    </span>
                    <span className="text-slate-200 group-hover:text-white transition-colors">
                      {cleanTitle(p.title)}
                    </span>
                    <span className="text-slate-700 mx-2">•</span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        <style>{`
          @keyframes taxsetu-marquee {
            from { transform: translateX(0); }
            to { transform: translateX(-50%); }
          }
        `}</style>
      </div>

      <PulseAdvisoryModal
        post={activePost}
        isOpen={!!activePost}
        onClose={() => setActivePost(null)}
        onOpenPermalink={(slug) => {
          setActivePost(null);
          navigate(`/pulse/${slug}`);
        }}
      />
    </>
  );
}
